// src/components/RPA/buttons/ConductComplianceTestingButton.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const POLL_MS = 8000;        // Poll every 8 seconds
const MAX_MINUTES = 15;      // Stop polling after 15 minutes

const ConductComplianceTestingButton = ({ docId, disabledHint, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // analysis result and/or status
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // progress & status
  const [phase, setPhase] = useState('idle');      // idle | queued | testing | done | error
  const [queuedMsg, setQueuedMsg] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const timerRef = useRef(null);
  const tickRef = useRef(null);

  // --- helpers --------------------------------------------------------------

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const closePanel = () => {
    stopTimers();
    setOpen(false);
  };

  // Poll /admin/rpa/docs for this docId, return doc or null
  const fetchDocMeta = async () => {
    const res = await axios.get(`${api}/admin/rpa/docs`, { withCredentials: true });
    const docs = Array.isArray(res?.data?.items) ? res.data.items : [];
    return docs.find(d => String(d._id) === String(docId)) || null;
  };

  // Polling loop when queued
  const beginPolling = () => {
    // elapsed timer
    tickRef.current = setInterval(() => {
      setElapsedSec(sec => sec + 1);
    }, 1000);

    // status poller
    timerRef.current = setInterval(async () => {
      try {
        const d = await fetchDocMeta();
        if (!d) return;

        // If complianceTestingResults present, we are done
        if (d.complianceTestingResults) {
          stopTimers();
          setPhase('done');
          setResult({
            success: true,
            compliance: { 
              azure: true, 
              results: d.complianceTestingResults 
            },
          });
          if (typeof onDone === 'function') onDone(); // refresh doc list
          return;
        }

        // Still queued/working – update the UI hints
        if (d.complianceTestingQueuedAt) {
          setPhase('testing');
          setLastUpdatedAt(d.updatedAt || d.createdAt || null);
        }

        // Safety stop after MAX_MINUTES
        if (elapsedSec >= MAX_MINUTES * 60) {
          stopTimers();
          setPhase('error');
          setErr('Compliance testing execution timed out. This may indicate complex regulatory analysis.');
        }
      } catch (e) {
        // transient errors – keep polling unless it persists too long
        console.warn('[RPA] compliance testing status poll failed:', e?.message || e);
      }
    }, POLL_MS);
  };

  const run = async () => {
    if (!docId) return;
    setBusy(true);
    setErr('');
    setResult(null);
    setElapsedSec(0);
    setLastUpdatedAt(null);
    stopTimers();

    try {
      // kick off compliance testing execution
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/conduct/compliance`,
        {},
        { withCredentials: true, validateStatus: () => true }
      );

      // 200 => testing is already available (cached) or completed inline
      if (res.status === 200) {
        setResult(res.data || { message: 'No data returned' });
        setPhase('done');
        setOpen(true);
        if (typeof onDone === 'function') onDone(); // refresh doc list
        return;
      }

      // 202 => queued; start polling
      if (res.status === 202) {
        setPhase('queued');
        setQueuedMsg(res.data?.message || 'Queued for compliance testing execution...');
        setOpen(true);
        beginPolling();
        return;
      }

      // other => error
      console.error('[RPA] conduct compliance testing unexpected:', res.status, res.data);
      setErr(res.data?.message || 'Compliance testing execution failed.');
      setPhase('error');
      setOpen(true);
    } catch (e) {
      console.error('[RPA] conduct compliance testing failed:', e);
      setErr('Compliance testing execution failed.');
      setPhase('error');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // Clean up timers if component unmounts or panel closes
  useEffect(() => {
    return () => stopTimers();
  }, []);

  // --- render helpers -------------------------------------------------------

  const renderSourceLabel = () => {
    if (!result) return null;

    // If Azure AI responded
    if (result.compliance?.azure) {
      return (
        <p style={{ color: 'green', fontWeight: 'bold', marginBottom: 6 }}>
          ✅ AI-Powered FWS WSFR Compliance Testing Complete
        </p>
      );
    }

    // Otherwise fallback heuristic
    return (
      <p style={{ color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
        ⚠️ Heuristic Compliance Testing (Fallback)
      </p>
    );
  };

  const renderComplianceResults = () => {
    if (!result?.compliance?.results) return null;

    let results;
    try {
      results = typeof result.compliance.results === 'string' 
        ? JSON.parse(result.compliance.results) 
        : result.compliance.results;
    } catch {
      return <p style={{ color: '#666' }}>Unable to parse compliance testing results</p>;
    }

    const execution = results.execution_summary || {};
    const overallResults = results.overall_compliance || {};
    const ruleResults = results.rule_compliance || [];
    const violations = results.violations || [];

    return (
      <div style={resultsContainer}>
        {/* Execution Summary */}
        <div style={executionBox}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
            FWS WSFR Compliance Testing Summary
          </h4>
          <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            <div><strong>Execution Date:</strong> {execution.execution_date ? new Date(execution.execution_date).toLocaleString() : 'Not specified'}</div>
            <div><strong>Records Analyzed:</strong> {execution.records_analyzed || 'Not specified'}</div>
            <div><strong>Total Amount:</strong> ${execution.total_amount?.toLocaleString() || '0.00'}</div>
            <div><strong>Testing Method:</strong> {execution.testing_method || 'Not specified'}</div>
            <div><strong>FedRAMP Compliant:</strong> {execution.fedramp_compliant ? '✅ Yes' : '❌ No'}</div>
          </div>
        </div>

        {/* Overall Compliance */}
        <div style={{ ...resultBox, border: getComplianceColor(overallResults.overall_status) }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
            Overall FWS WSFR Compliance
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
            <div><strong>Rules Tested:</strong> {overallResults.total_rules || 0}</div>
            <div><strong>Compliant Rules:</strong> {overallResults.compliant_rules || 0}</div>
            <div><strong>Violations Found:</strong> {overallResults.total_violations || 0}</div>
            <div><strong>Compliance Rate:</strong> {overallResults.compliance_rate || '0%'}</div>
            <div><strong>Overall Status:</strong> {overallResults.overall_status || 'Unknown'}</div>
            <div><strong>Risk Level:</strong> {overallResults.risk_level || 'Unknown'}</div>
          </div>
          
          {overallResults.audit_opinion && (
            <div style={{ marginTop: 8, padding: 8, background: '#fef2f2', borderRadius: 4, border: '1px solid #f87171' }}>
              <strong style={{ fontSize: 12, color: '#991b1b' }}>Audit Opinion:</strong>
              <div style={{ fontSize: 11, color: '#991b1b', marginTop: 2 }}>
                {overallResults.audit_opinion}
              </div>
            </div>
          )}
        </div>

        {/* Rule Compliance Results */}
        {ruleResults.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Rule Compliance Results ({ruleResults.length})
            </h4>
            <div style={attributesGrid}>
              {ruleResults.slice(0, 4).map((rule, idx) => (
                <div key={idx} style={attributeCard}>
                  <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
                    {rule.rule_id || `Rule ${idx + 1}`}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#374151', marginBottom: 4 }}>
                    {rule.rule_description}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 10, marginBottom: 6 }}>
                    <div><strong>Tested:</strong> {rule.records_tested || 0}</div>
                    <div><strong>Compliant:</strong> {rule.compliant_records || 0}</div>
                    <div><strong>Violations:</strong> {rule.violation_count || 0}</div>
                    <div><strong>Rate:</strong> {rule.compliance_rate || '0%'}</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
                    {rule.conclusion}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ 
                      ...badge, 
                      background: rule.violation_count > 0 ? '#fef2f2' : '#f0fdf4',
                      color: rule.violation_count > 0 ? '#991b1b' : '#065f46'
                    }}>
                      {rule.violation_count > 0 ? 'NON-COMPLIANT' : 'COMPLIANT'}
                    </span>
                    {rule.requires_remediation === 'yes' && (
                      <span style={{ ...badge, background: '#fef3c7', color: '#92400e' }}>
                        REMEDIATION
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {ruleResults.length > 4 && (
                <div style={{ ...attributeCard, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    +{ruleResults.length - 4} more rules...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Violations */}
        {violations.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1f2937' }}>
              Compliance Violations ({violations.length})
            </h4>
            <div style={recommendationsContainer}>
              {violations.slice(0, 3).map((violation, idx) => (
                <div key={idx} style={violationItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ 
                      ...badge, 
                      background: getSeverityColor(violation.severity),
                      color: getSeverityTextColor(violation.severity)
                    }}>
                      {violation.severity?.toUpperCase() || 'MEDIUM'}
                    </span>
                    <span style={{ fontSize: 10, color: '#dc2626' }}>Rule: {violation.rule_id}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#374151', marginBottom: 4 }}>
                    {violation.description}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>
                    Amount: ${violation.amount?.toLocaleString() || '0.00'}
                  </div>
                  {violation.remediation_required && (
                    <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>
                      Remediation Required: {violation.remediation_action}
                    </div>
                  )}
                </div>
              ))}
              {violations.length > 3 && (
                <div style={{ fontSize: 11, color: '#6b7280', padding: 8 }}>
                  +{violations.length - 3} more violations...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Success Message */}
        {ruleResults.length === 0 && violations.length === 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', borderRadius: 6, border: '1px solid #22c55e' }}>
            <strong style={{ fontSize: 12, color: '#15803d' }}>FWS WSFR Compliance Testing Complete</strong>
            <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
              No compliance violations identified. All FWS WSFR rules appear to be satisfied.
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStatus = () => {
    if (phase === 'queued') {
      return (
        <div style={statusBox}>
          <strong>Queued for FWS WSFR Compliance Testing</strong>
          <div style={{ fontSize: 13, color: '#555' }}>{queuedMsg}</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              Preparing compliance analysis... {formatElapsed(elapsedSec)}
            </span>
          </div>
        </div>
      );
    }

    if (phase === 'testing') {
      return (
        <div style={statusBox}>
          <strong>Analyzing FWS WSFR Compliance...</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
            Applying federal compliance rules to financial data and analyzing violations
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: '#777' }}>
              This can take several minutes for large datasets. Elapsed {formatElapsed(elapsedSec)}
              {lastUpdatedAt ? ` • Last update ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
          <ProgressBar />
        </div>
      );
    }

    if (phase === 'error') {
      return <p style={{ color: 'crimson' }}>{err || 'Something went wrong.'}</p>;
    }

    return null;
  };

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        disabled={busy || !!disabledHint}
        title={disabledHint || 'Execute FWS WSFR compliance testing on financial data'}
        style={{ 
          background: '#0ea5e9', 
          color: 'white', 
          border: 'none', 
          padding: '4px 8px', 
          borderRadius: 4,
          fontSize: 12,
          cursor: disabledHint ? 'not-allowed' : 'pointer',
          opacity: disabledHint ? 0.6 : 1
        }}
      >
        {busy ? 'Testing…' : 'FWS Compliance'}
      </button>

      {open && (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>FWS WSFR Compliance Testing</strong>
            <button onClick={closePanel} style={{ padding: '2px 8px' }}>Close</button>
          </div>

          <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
            Test uploaded financial data against FWS Wildlife Sport Fish Restoration compliance rules
          </div>

          {phase === 'idle' && (
            <button
              onClick={run}
              disabled={busy}
              style={{ 
                background: '#0ea5e9', 
                color: 'white', 
                border: 'none', 
                padding: '6px 12px', 
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer'
              }}
            >
              {busy ? 'Starting...' : 'Execute Compliance Testing'}
            </button>
          )}

          {renderStatus()}

          {/* Show the final result once ready */}
          {phase === 'done' && !err && result && (
            <>
              {renderSourceLabel()}
              {renderComplianceResults()}
              
              {/* Raw JSON for debugging */}
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
                  Raw Results (Debug)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0', fontSize: 10, background: '#f8f9fa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                  {typeof result.compliance.results === 'string' 
                    ? result.compliance.results 
                    : JSON.stringify(result.compliance.results, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- helpers & styles ---------------------------- */

function getComplianceColor(status) {
  switch (status) {
    case 'compliant': return '1px solid #22c55e';
    case 'partially_compliant': return '1px solid #f59e0b';
    case 'non_compliant': return '1px solid #ef4444';
    default: return '1px solid #e5e7eb';
  }
}

function getSeverityColor(severity) {
  switch (severity) {
    case 'critical': return '#fef2f2';
    case 'high': return '#fef3c7';
    case 'medium': return '#eff6ff';
    case 'low': return '#f0fdf4';
    default: return '#f3f4f6';
  }
}

function getSeverityTextColor(severity) {
  switch (severity) {
    case 'critical': return '#991b1b';
    case 'high': return '#92400e';
    case 'medium': return '#1e40af';
    case 'low': return '#065f46';
    default: return '#6b7280';
  }
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const panel = {
  marginTop: 8,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 12,
  borderRadius: 8,
  maxWidth: 900,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const statusBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 10,
  borderRadius: 8,
  marginBottom: 10
};

const resultsContainer = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8,
  marginBottom: 8
};

const executionBox = {
  background: '#f0f9ff',
  border: '1px solid #0ea5e9',
  padding: 10,
  borderRadius: 6,
  marginBottom: 12
};

const resultBox = {
  background: '#fff',
  padding: 10,
  borderRadius: 6,
  marginBottom: 12
};

const attributesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 8
};

const attributeCard = {
  background: '#f0f9ff',
  border: '1px solid #0ea5e9',
  padding: 10,
  borderRadius: 6,
  minHeight: 120
};

const recommendationsContainer = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8
};

const violationItem = {
  background: '#fef2f2',
  border: '1px solid #ef4444',
  padding: 10,
  borderRadius: 6
};

const badge = {
  fontSize: 9,
  padding: '2px 6px',
  borderRadius: 12,
  fontWeight: 'bold',
  textTransform: 'uppercase'
};

const Spinner = () => (
  <span
    style={{
      width: 14,
      height: 14,
      border: '2px solid #cbd3e1',
      borderTop: '2px solid #0ea5e9',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 1s linear infinite'
    }}
  />
);

const ProgressBar = () => (
  <div style={{ marginTop: 8, height: 8, background: '#e9edf5', borderRadius: 6, overflow: 'hidden' }}>
    <div style={{
      width: '70%',
      height: '100%',
      background: '#0ea5e9',
      animation: 'bar 3s ease-in-out infinite'
    }} />
    <style>{`
      @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
      @keyframes bar {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(30%); }
        100% { transform: translateX(150%); }
      }
    `}</style>
  </div>
);

export default ConductComplianceTestingButton;




// // src/components/RPA/buttons/ConductFWSComplianceTestingButton.js
// import React, { useState } from 'react';
// import axios from 'axios';

// const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// export default function ConductFWSComplianceTestingButton({ docId, onDone, disabledHint }) {
//   const [loading, setLoading] = useState(false);
//   const [hint, setHint] = useState('');

//   const run = async () => {
//     if (!docId || loading) return;
//     setHint('');
//     setLoading(true);
//     try {
//       const res = await axios.post(
//         `${API_URL}/admin/rpa/docs/${docId}/conduct/compliance`,
//         {},
//         { withCredentials: true }
//       );
//       // 202 = queued; 200 = already had results
//       const msg = res?.data?.message || (res?.status === 200 ? 'Already completed' : 'Queued');
//       setHint(msg);
//       onDone?.();
//     } catch (e) {
//       setHint(e?.response?.data?.message || 'Compliance testing failed to start');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
//       <button
//         onClick={run}
//         disabled={loading || !!disabledHint}
//         title={disabledHint || 'Run FWS compliance testing'}
//         style={{
//           background: '#0ea5e9',
//           color: 'white',
//           border: '1px solid #0284c7',
//           padding: '4px 8px',
//           borderRadius: 6,
//           fontSize: 11,
//           cursor: loading || disabledHint ? 'not-allowed' : 'pointer',
//           opacity: loading || disabledHint ? 0.65 : 1,
//         }}
//       >
//         {loading ? 'Queuing…' : 'Run FWS Compliance'}
//       </button>
//       {hint ? <span style={{ fontSize: 10, color: '#0369a1' }}>{hint}</span> : null}
//     </div>
//   );
// }
